import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { FilterDropdownService } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.service';
import { SearchService } from '../../../../../ui-components/lib/search-field/search.service';
import { StatComponent } from '../../../../../ui-components/lib/stat/stat.component';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { SearchEnum } from '../../../../common/enums/search.enum';
import { Insight } from '../../../../common/models/insight.model';
import { InsightsPanelComponent } from '../../../../common/components/insights-panel/insights-panel.component';
import { injectFloatingLayout } from '../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { MyInboxService } from '../../../../common/services/my-inbox/my-inbox.service';
import { ASSIGNED_TO_ME_INSIGHTS } from '../assigned-to-me/assigned-to-me.constants';
import { EmployeeAssignedTasksAdapter } from '../assigned-to-me/employee-assigned-tasks.adapter';
import {
  DELEGATED_TO_ME_INSIGHTS,
  DELEGATED_TO_ME_INSIGHT_KEYS,
} from '../delegated-to-me/delegated-to-me.constants';
import { AssignedToMeComponent } from '../assigned-to-me/assigned-to-me.component';
import { DelegatedToMeComponent } from '../delegated-to-me/delegated-to-me.component';

const ASSIGNED_INSIGHT_KEYS = [
  'total_current_assigned_tasks',
  'total_current_overdue_tasks',
  'total_on_hold_tasks_count',
] as const;

@Component({
  selector: 'lib-assigned-inbox',
  imports: [
    TranslocoPipe,
    StatComponent,
    InsightsPanelComponent,
    AssignedToMeComponent,
    DelegatedToMeComponent,
  ],
  templateUrl: './assigned-inbox.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AssignedInboxComponent {
  readonly assignedCount = input(0);
  readonly delegatedCount = input(0);
  readonly counterRefresh = output<void>();

  private readonly searchService = inject(SearchService);
  private readonly filterService = inject(FilterDropdownService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly myInboxService = inject(MyInboxService);
  private readonly assignedTasksAdapter = inject(EmployeeAssignedTasksAdapter);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlags = inject(FeatureFlagsService);
  protected readonly floating = injectFloatingLayout();

  readonly inboxTabRoutingParam = EMPLOYEE_ROUTES.MY_INBOX.TABS;
  readonly subTabQueryParam = EMPLOYEE_ROUTES.MY_INBOX.SUB_TAB_QUERY_PARAM;
  readonly activeSubTab = signal<string>(
    EMPLOYEE_ROUTES.MY_INBOX.TABS.assignedToMe
  );
  readonly isAssignedTab = computed(
    () => this.activeSubTab() === this.inboxTabRoutingParam.assignedToMe
  );
  private readonly systemDefaultColor = computed(() =>
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_default_color
    )
  );
  readonly assignedTabClass = computed(() =>
    this.subTabClass(this.isAssignedTab())
  );
  readonly delegatedTabClass = computed(() =>
    this.subTabClass(!this.isAssignedTab())
  );
  readonly activeInsights = signal<Insight[]>(
    ASSIGNED_TO_ME_INSIGHTS.map(insight => ({ ...insight }))
  );
  private hasLoadedInsights = false;

  constructor() {
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe(params => {
        const previousSubTab = this.activeSubTab();
        this.syncSubTabFromQuery(params.get(this.subTabQueryParam));
        if (!this.hasLoadedInsights || previousSubTab !== this.activeSubTab()) {
          this.hasLoadedInsights = true;
          this.loadInsights(this.isAssignedTab());
        }
      });
  }

  private subTabClass(active: boolean): string {
    const base = 'min-w-[3.125rem] rounded-md py-sm px-md font-medium text-sm';
    if (!active) {
      return `${base} text-neutral-700`;
    }
    return this.systemDefaultColor()
      ? `${base} bg-primary-800 text-white`
      : `${base} bg-primary-50 text-primary-700`;
  }

  onInsightsChange(insights: Insight[]): void {
    this.activeInsights.set(insights);
  }

  syncSubTabFromQuery(subTab: string | null): void {
    if (subTab === this.inboxTabRoutingParam.taskDelegation) {
      this.activeSubTab.set(this.inboxTabRoutingParam.taskDelegation);
      return;
    }
    if (subTab === this.inboxTabRoutingParam.assignedToMe) {
      this.activeSubTab.set(this.inboxTabRoutingParam.assignedToMe);
    }
  }

  selectSubTab(tab: string): void {
    if (this.activeSubTab() === tab) {
      return;
    }

    this.searchService.clearSearch();
    this.activeSubTab.set(tab);
    this.loadInsights(this.isAssignedTab());

    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    delete queryParams[SearchEnum.keyword];
    delete queryParams[SearchEnum.search];

    this.filterService.resetFiltersAndQueryParams(
      this.router,
      this.activatedRoute,
      {
        ...queryParams,
        [this.subTabQueryParam]: tab,
      }
    );
  }

  private loadInsights(isAssigned: boolean): void {
    const definitions = isAssigned
      ? ASSIGNED_TO_ME_INSIGHTS
      : DELEGATED_TO_ME_INSIGHTS;
    const keys = isAssigned
      ? ASSIGNED_INSIGHT_KEYS
      : DELEGATED_TO_ME_INSIGHT_KEYS;
    const request$ = isAssigned
      ? this.assignedTasksAdapter.getInsights()
      : this.myInboxService.getDelegatedToMeInsights();

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: { data: Insight[] }) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        this.activeInsights.set(
          definitions.map((insight, index) => ({
            ...insight,
            value:
              data
                .find((item: Insight) => item.key === keys[index])
                ?.value?.toString() ?? '0',
          }))
        );
      });
  }
}
